/**
 * Accessible TMenu Enhancer (Keyboard Navigation + ARIA + Focus Fix)
 * Final Stable Version
 *
 * Please note : As the menu itself is too complex, this is just a wrapper code
 * that gives it keyboard functionality. The actual fully accessible implementation
 * would require a complete rewrite of the current TMenu structure.
 *
 * Author: Khushwant
 */


(function () {
  'use strict';

  const MAX_RETRIES = 30;
  const INITIAL_DELAY = 400;
  const ACTIVE_CLASS = 'hover';
  const MENU_SELECTOR = '.tmenu_nav';
  const ROOT_SELECTOR = '.tmenu_item.tmenu_item--root';

  function log(...args) {
    console.log('[TMenu A11y]', ...args);
  }

  function warn(...args) {
    console.warn('[TMenu A11y]', ...args);
  }

  function error(...args) {
    console.error('[TMenu A11y]', ...args);
  }

  function simulateMouseHover(li, triggerElement) {
    const mouseEvents = ['mouseenter', 'mouseover'];
    mouseEvents.forEach(type => {
      li.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      triggerElement.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      log(`🖱️ Dispatched ${type} on <li> and trigger`);
    });
  }

  function simulateMouseLeave(li, triggerElement) {
    const mouseEvents = ['mouseleave', 'mouseout'];
    mouseEvents.forEach(type => {
      li.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      triggerElement.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      log(`👋 Dispatched ${type} on <li> and trigger`);
    });
  }

  function openSubmenu(item, triggerElement = null) {
    const link = item.querySelector(':scope > a.tmenu_item_link');
    if (!link) return;

    item.classList.add(ACTIVE_CLASS);
    item.setAttribute('data-open', 'true');
    link.setAttribute('aria-expanded', 'true');

    const trigger = triggerElement || link;
    simulateMouseHover(item, trigger);

    log('✅ Opened submenu for:', link.textContent.trim());
  }

  function closeSubmenu(item) {
    const link = item.querySelector(':scope > a.tmenu_item_link');
    const indicator = item.querySelector(':scope > a .tmenu_indicator') || link;
    if (!link || !indicator) return;

    item.classList.remove(ACTIVE_CLASS);
    item.setAttribute('data-open', 'false');
    link.setAttribute('aria-expanded', 'false');

    simulateMouseLeave(item, indicator);
    log('❎ Closed submenu for:', link.textContent.trim());
  }

  function enhanceItem(item, index) {
    if (item.dataset.a11yEnhanced === 'true') {
      log(`Item ${index + 1} already enhanced, skipping.`);
      return;
    }

    const link = item.querySelector(':scope > a.tmenu_item_link');
    const indicator = item.querySelector(':scope > a .tmenu_indicator');

    if (!link) {
      warn(`Item ${index + 1} has no <a>. Skipping.`);
      return;
    }

    const hasHref = link.hasAttribute('href') && link.getAttribute('href') !== 'javascript:;';
    const triggerElement = hasHref ? indicator : link;

    if (!triggerElement) {
      warn(`Item ${index + 1} has no valid trigger. Skipping.`);
      return;
    }

    // Mark as enhanced and set state
    item.dataset.a11yEnhanced = 'true';
    item.setAttribute('data-open', 'false');

    // Set accessibility attributes
    triggerElement.setAttribute('tabindex', '0');
    triggerElement.setAttribute('role', 'button');
    triggerElement.setAttribute('aria-label', 'Expand submenu');
    link.setAttribute('aria-haspopup', 'true');

    // Toggle submenu on Enter/Space
    triggerElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const isOpen = item.getAttribute('data-open') === 'true';
        if (isOpen) {
          log(`⬅️ Closing submenu for "${link.textContent.trim()}"`);
          closeSubmenu(item);
        } else {
          log(`🡆 Opening submenu for "${link.textContent.trim()}"`);
          openSubmenu(item, triggerElement);
        }
      }
    });

    // Close submenu on focus leave
    item.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!item.contains(document.activeElement)) {
          log(`🔄 Focus left "${link.textContent.trim()}" → closing submenu`);
          closeSubmenu(item);
        }
      }, 10);
    });

    log(`🎯 Enhanced item ${index + 1}: "${link.textContent.trim()}" using trigger:`, hasHref ? 'indicator' : 'anchor');
  }

  function enhanceMenu() {
    const menu = document.querySelector(MENU_SELECTOR);
    if (!menu) {
      warn('🚫 Menu container not found.');
      return false;
    }

    const rootItems = menu.querySelectorAll(ROOT_SELECTOR);
    if (!rootItems.length) {
      warn('🚫 No root items found in menu.');
      return false;
    }

    log(`🚀 Enhancing ${rootItems.length} menu items...`);
    rootItems.forEach(enhanceItem);
    return true;
  }

  function waitForMenu(attempt = 1, delay = INITIAL_DELAY) {
    const menuReady = document.querySelector(MENU_SELECTOR);
    const rootReady = document.querySelectorAll(ROOT_SELECTOR).length > 0;

    if (menuReady && rootReady) {
      log(`✅ Menu ready on attempt ${attempt}. Enhancing now...`);
      enhanceMenu();
      return;
    }

    if (attempt >= MAX_RETRIES) {
      error(`❌ Enhancement failed after ${MAX_RETRIES} attempts.`);
      return;
    }

    log(`⏳ Attempt ${attempt}: Menu not ready. Retrying in ${delay}ms...`);
    setTimeout(() => waitForMenu(attempt + 1, delay * 1.25), delay);
  }

  log('🔧 Starting TMenu accessibility enhancement...');
  waitForMenu();
})();